/* eslint-disable @typescript-eslint/no-unused-expressions */
import type {CaseReducer, PayloadAction} from '@reduxjs/toolkit'

import type {PipelineId} from '../../../../../types'
import {generateFreeName} from '../../../../../utils/generateFreeName'

import type {PipelineDraftState} from './EditPipelinePage.slices.types'
import {deleteIfEmpty, getJob} from './EditPipelinePage.slices.utils'

export const setJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
    value: string
  }>
> = (state, action) => {
  const {pipelineId, jobId, savedName, value} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job.parameters ??= {}
    job.parameters[savedName] = value
  }
}

export const renameJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    name: string
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, savedName, name} = action.payload

  if (name !== savedName) {
    state[pipelineId] ??= {}
    state[pipelineId]!.renamed ??= {}
    state[pipelineId]!.renamed!.jobParameters ??= {}
    state[pipelineId]!.renamed!.jobParameters![savedName] = name
  } else if (state[pipelineId]?.renamed?.jobParameters?.[savedName] != null) {
    delete state[pipelineId]!.renamed!.jobParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'jobParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  }
}

export const deleteJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{
    pipelineId: PipelineId
    jobId: string
    savedName: string
  }>
> = (state, action) => {
  const {pipelineId, jobId, savedName} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (state[pipelineId]?.added?.jobParameters?.[savedName]) {
    delete job?.parameters?.[savedName]
    delete state[pipelineId]!.added!.jobParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.added, 'jobParameters') &&
      deleteIfEmpty(state[pipelineId], 'added')
    delete state[pipelineId]!.renamed!.jobParameters![savedName]
    deleteIfEmpty(state[pipelineId]!.renamed, 'jobParameters') &&
      deleteIfEmpty(state[pipelineId], 'renamed')
  } else {
    state[pipelineId] ??= {}
    state[pipelineId]!.deleted ??= {}
    state[pipelineId]!.deleted!.jobParameters ??= []
    state[pipelineId]!.deleted!.jobParameters!.push(savedName)
  }
}

export const restoreJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; savedName: string}>
> = (state, action) => {
  const {pipelineId, savedName} = action.payload
  const {deleted} = state[pipelineId] ?? {}

  if (deleted?.jobParameters?.includes(savedName)) {
    deleted.jobParameters = deleted?.jobParameters.filter(item => item !== savedName)
    deleteIfEmpty(deleted, 'jobParameters') && deleteIfEmpty(state[pipelineId], 'deleted')
  }
}

export const addJobParameter: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string; prefix?: string}>
> = (state, action) => {
  const {pipelineId, prefix = '', jobId} = action.payload
  const job = getJob(state, pipelineId, jobId)

  if (job != null) {
    job.parameters ??= {}
    const key = generateFreeName({
      getName: iteration => `parameter_${jobId}_${iteration}`,
      usedNames: Object.keys(job.parameters),
      errorMessage: 'Cannot generate unique parameter key',
    })
    job.parameters[key] = ''
    renameJobParameter(state, {type: '', payload: {pipelineId, name: prefix, savedName: key}})
    state[pipelineId]!.added ??= {}
    state[pipelineId]!.added!.jobParameters ??= {}
    state[pipelineId]!.added!.jobParameters![key] = true
  }
}

export const clearAddedJobParameters: CaseReducer<
  PipelineDraftState,
  PayloadAction<{pipelineId: PipelineId; jobId: string}>
> = (state, action) => {
  const {pipelineId, jobId} = action.payload
  const {added} = state[pipelineId] ?? {}
  const job = getJob(state, pipelineId, jobId)

  if (added?.jobParameters != null && job != null) {
    Object.keys(added.jobParameters).forEach(key => {
      delete job.parameters?.[key]
    })
    delete added.jobParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}

export const applyAddedJobParameters: CaseReducer<PipelineDraftState, PayloadAction<string>> = (
  state,
  action,
) => {
  const pipelineId = action.payload
  const {added} = state[pipelineId] ?? {}

  if (added?.jobParameters != null) {
    delete added.jobParameters
    deleteIfEmpty(state[pipelineId], 'added')
  }
}
